import type { Optional } from '@festina-life/core'
import { RawSelect } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { ConfirmationBox } from '@festina-life/ui/structure'
import { useMemo, useState } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import type {
  NonConfigurationSimpleValueField,
  ValueConfig,
  ValueSetupFieldValue,
} from '../../types/templateModel'
import { generateEmptyFieldValue } from '../../uiModels/createEmptyFieldHelpers'
import { InheritedValueCopier } from '../copyInheritedValue/InheritedValueCopier'

type Props = {
  field: NonConfigurationSimpleValueField
  isExpanded?: boolean
}

export const TemplateFieldConfig = ({ field, isExpanded }: Props) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const translate = useTranslate()
  const { isDraftTemplate, templateVersion } = useTemplateContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)
  const configValue = field.value?.valueConfig?.value

  const optionsWithTranslations = useMemo(
    () =>
      ({
        fixed: translate('enum.valueSetupConfig.fixed'),
        selectable: translate('enum.valueSetupConfig.selectable'),
        notRelevant: translate('enum.valueSetupConfig.notRelevant'),
      }) as Record<ValueConfig, string>,
    [translate],
  )
  const options = useMemo(
    () => Object.keys(optionsWithTranslations) as ValueConfig[],
    [optionsWithTranslations],
  )

  const isFieldEditable = isExpanded && isDraftTemplate

  const configValueOrigin = field.value?.valueConfig?.origin
  const isInheritedValue =
    !!configValueOrigin &&
    configValueOrigin !== `${field.value?.path}.valueConfig`
  const isExpressionTruthy = !!field.value?.valueConfig?.value

  const isEditingBlockedForInheritedValue =
    isFieldEditable && isInheritedValue && isExpressionTruthy

  const handleUpdateConfigValue = (nextConfig: Optional<ValueConfig>) => {
    if (nextConfig) {
      if (nextConfig !== 'notRelevant') {
        void performConfigFieldUpdate(nextConfig)
      } else {
        setIsConfirmationModalOpen(true)
      }
    }
  }

  const performConfigFieldUpdate = (nextConfig: ValueConfig) => {
    const nextValue =
      field.value && nextConfig !== 'notRelevant'
        ? field.value
        : generateEmptyFieldValue({
            field,
            meta: metaModel,
          })

    return (field.update as (v: ValueSetupFieldValue) => Promise<void>)({
      ...nextValue,
      documentation: field.value?.documentation,
      valueConfig: {
        value: nextConfig,
        origin: `${nextValue.path}.valueConfig`,
      },
    })
  }

  const handleConfirmSetNotRelevant = async () => {
    await performConfigFieldUpdate('notRelevant')
    setIsConfirmationModalOpen(false)
  }

  return isFieldEditable ? (
    <>
      <ConfirmationBox
        isOpen={isConfirmationModalOpen}
        onCancel={() => setIsConfirmationModalOpen(false)}
        title={translate(
          'templateViewer.fieldDetailsModal.field.config.warningTitle',
          { fieldName: field.name },
        )}
        onAccept={handleConfirmSetNotRelevant}
      >
        <p>
          {translate(
            'templateViewer.fieldDetailsModal.field.config.warningDescription',
            { fieldName: field.name },
          )}
        </p>
      </ConfirmationBox>
      <div className="group flex flex-row items-center gap-x-2">
        <RawSelect
          options={options}
          shouldShowClearButton={!field.value?.valueConfig?.value}
          placeholder={translate('none')}
          renderFn={(option) => (option ? optionsWithTranslations[option] : '')}
          onChange={handleUpdateConfigValue}
          value={configValue ?? null}
          className="max-w-fit text-left"
          disabled={
            isEditingBlockedForInheritedValue || field.isParentNotRelevant
          }
        />
        {isEditingBlockedForInheritedValue && (
          <InheritedValueCopier
            onCopy={() =>
              handleUpdateConfigValue(field.value?.valueConfig?.value)
            }
            fromOrigin={configValueOrigin}
            fieldPath={field.value?.path ?? ''}
            hoverButtonClassName="relative! inset-0!"
          />
        )}
      </div>
    </>
  ) : (
    <div className="text-sm italic opacity-60">{configValue ?? '-'}</div>
  )
}
